/**
 * ConfirmProfileDialog — reusable "Confirm this profile" modal.
 *
 * One dialog, two callers:
 *   1. SocialOutreachButtons (verified-link first-open flow):
 *      wrong  → clear the stored URL, log a mark-wrong entry
 *      correct → verify + optional follow
 *   2. PendingSocialsBadge (Cleanup Step 8 / enrichment candidate review):
 *      wrong  → reject the pending candidate
 *      correct → accept the pending candidate ("add to profile"); the
 *      second Correct button (when shown) does the same accept AND ALSO
 *      marks the platform as followed — mirroring the verify UX so
 *      operators only have to think about one button pattern regardless
 *      of whether the URL is confirmed or still pending.
 *
 * Callers own the async action; the dialog only handles the shell + UX.
 */
import React, { useState, useEffect } from "react";
import { Loader2, ShieldCheck, AlertTriangle, Unlink, UserCheck, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ConfirmProfileDialog({
  open,
  onOpenChange,
  busy = false,
  platformLabel,
  realtor,
  url,
  contextNote,                 // Optional italic amber line under the Link (e.g. Cleanup demoted-note)
  wrongLabel = "Wrong profile",
  wrongDescription,            // Optional replacement for the default "if this looks like a different X…"
  correctLabel = "Correct — mark verified",
  correctAndFollowLabel,       // When set + onCorrectAndFollow present → second Correct button renders
  onWrong,
  onCorrect,
  onCorrectAndFollow,
  onChangeLink,                // (newUrl) => Promise — when set, "Change This Link" button appears
  testidPrefix = "confirm-profile",
}) {
  const [editing, setEditing] = useState(false);
  const [newUrl, setNewUrl] = useState("");

  // Reset edit state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setEditing(false);
      setNewUrl("");
    }
  }, [open]);

  const displayName = realtor?.name
    || `${realtor?.first_name || ""} ${realtor?.last_name || ""}`.trim()
    || "this realtor";
  const firstName = realtor?.first_name || "this realtor";
  const showFollow = !!(onCorrectAndFollow && correctAndFollowLabel);
  const showChangeLink = !!onChangeLink;

  const handleSaveNewLink = async (alsoFollow = false) => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    await onChangeLink(trimmed, { alsoFollow });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!busy) onOpenChange(o); }}
    >
      <DialogContent
        className="max-w-md w-[calc(100vw-2rem)] p-4 sm:p-6"
        data-testid={`${testidPrefix}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Confirm this profile
          </DialogTitle>
          <DialogDescription>
            We just opened the {platformLabel} profile in a new tab — take a
            quick look and confirm it belongs to{" "}
            <span className="font-semibold text-slate-900">{displayName}</span>
            {realtor?.brokerage_name ? (
              <> at <span className="font-semibold text-slate-900">{realtor.brokerage_name}</span></>
            ) : null}
            .
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
              Link
            </div>
            {showChangeLink && !editing && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-rose-600 hover:text-rose-800 hover:bg-rose-50"
                onClick={() => { setEditing(true); setNewUrl(""); }}
                disabled={busy}
                data-testid={`${testidPrefix}-change-link-btn`}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Change this link
              </Button>
            )}
          </div>
          {editing ? (
            <div className="space-y-2">
              <p className="text-xs text-slate-600">
                Paste the correct {platformLabel} link below. The old link will be logged as incorrect.
              </p>
              <Input
                autoFocus
                placeholder={`https://www.${platformLabel.toLowerCase()}.com/...`}
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                className="font-mono text-sm"
                data-testid={`${testidPrefix}-new-url-input`}
              />
              <div className="flex flex-wrap items-center gap-2">
                {showFollow && (
                  <Button
                    size="sm"
                    disabled={busy || !newUrl.trim()}
                    onClick={() => handleSaveNewLink(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`${testidPrefix}-save-new-link-follow`}
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <UserCheck className="h-4 w-4 mr-1" />
                    )}
                    Save &amp; Verified &amp; Followed
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={showFollow ? "outline" : "default"}
                  disabled={busy || !newUrl.trim()}
                  onClick={() => handleSaveNewLink(false)}
                  className={showFollow ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "bg-emerald-600 hover:bg-emerald-700"}
                  data-testid={`${testidPrefix}-save-new-link`}
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4 mr-1" />
                  )}
                  Save &amp; Verified
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => { setEditing(false); setNewUrl(""); }}
                  data-testid={`${testidPrefix}-cancel-change`}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-mono text-slate-900 break-all hover:underline"
                data-testid={`${testidPrefix}-url`}
              >
                {url}
              </a>
              {contextNote ? (
                <div
                  className="text-[11px] italic text-amber-700 leading-snug pt-1"
                  data-testid={`${testidPrefix}-context`}
                >
                  {contextNote}
                </div>
              ) : null}
            </>
          )}
          {!editing && (
            <p className="text-xs text-slate-600 flex items-start gap-1.5 pt-1">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              {wrongDescription || (
                <span>
                  If this looks like a different {firstName} (wrong city,
                  wrong brokerage, wrong face), tap <strong>{wrongLabel}</strong>{" "}
                  — we&apos;ll clear the link and flag it for re-enrichment.
                </span>
              )}
            </p>
          )}
        </div>
        {!editing && (
          <div className="flex flex-col-reverse sm:flex-row sm:items-stretch sm:justify-between gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 sm:self-center sm:shrink-0"
              disabled={busy}
              onClick={onWrong}
              data-testid={`${testidPrefix}-wrong`}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Unlink className="h-4 w-4 mr-1" />
              )}
              {wrongLabel}
            </Button>
            <div className="flex flex-col gap-2 min-w-0 sm:flex-1">
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={onCorrect}
                className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                data-testid={`${testidPrefix}-correct`}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4 mr-1" />
                )}
                <span className="truncate">{correctLabel}</span>
              </Button>
              {showFollow ? (
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={onCorrectAndFollow}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`${testidPrefix}-correct-follow`}
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <UserCheck className="h-4 w-4 mr-1" />
                  )}
                  <span className="truncate">{correctAndFollowLabel}</span>
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
